// src/redux/userPreferencesSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../service/apiService";
import { getUserId } from "../utils";
import { RootState } from "./store";
import {
  MRT_ColumnDef,
  MRT_SortingState,
  MRT_PaginationState,
} from "material-react-table";
import defaultTableData from "../shared/tableConfig.json";

const API_BASE_URL = process.env.REACT_APP_BASE_URL + "user/profile";

// Enhanced Types with proper MRT compatibility
interface TableSortingConfig {
  enableMultiSort: boolean;
  sortableColumns: string[];
  defaultSort: MRT_SortingState;
}

interface TableInitialState {
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  sorting?: MRT_SortingState;
  pagination?: MRT_PaginationState;
  density?: "compact" | "normal" | "spacious";
  showColumnFilters?: boolean;
  showGlobalFilter?: boolean;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  [key: string]: any;
}

// Enhanced TableContextConfig with proper MRT_ColumnDef types
interface TableContextConfig {
  columns: Array<
    MRT_ColumnDef<any> & {
      // Allow string Cell values that will be converted to components
      Cell?: string | ((props: any) => React.ReactNode);
    }
  >;
  extends?: string;
  initialState?: TableInitialState;
  enablePagination?: boolean;
  enableMultiRowSelection?: boolean;
  enableColumnFilters?: boolean;
  enableSorting?: boolean;
  manualSorting?: boolean;
  enableMultiSort?: boolean;
  sortingConfig?: TableSortingConfig;
  // Add other MRT table props as needed
  enableStickyHeader?: boolean;
  enableColumnOrdering?: boolean;
  enableColumnDragging?: boolean;
  enableRowSelection?: boolean;
  enableColumnActions?: boolean;
  enableGlobalFilter?: boolean;
  enableDensityToggle?: boolean;
  enableExpandAll?: boolean;
  enableExpanding?: boolean;
  enableColumnResizing?: boolean;
  manualPagination?: boolean;
  muiTableProps?: any;
  muiTableHeadCellProps?: any;
  muiTableHeadCellContentProps?: any;
  muiTableBodyRowProps?: any;
}

// Enhanced context specific type with index signature
interface ContextSpecificTables {
  [tableName: string]: TableContextConfig;
}

interface UserSettings {
  tables: {
    defaultConfig: any;
    contextSpecific: ContextSpecificTables;
  };
  rowActions?: any;
  columnEnhancements?: any;
}

interface UserPreferencesState {
  preferences: any;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: UserPreferencesState = {
  preferences: {
    tables: {
      defaultConfig: {},
      contextSpecific: {},
    },
  },
  status: "idle",
  error: null,
};

// Helper function to convert string Cell values to proper components
const convertColumnCellFunctions = (
  columns: any[]
): Array<MRT_ColumnDef<any>> => {
  return columns.map((col) => {
    if (col.Cell && typeof col.Cell === "string") {
      // These will be handled in the component layer
      // For now, keep as string and component will convert
      return col;
    }
    return col;
  });
};

// Enhanced deep merge with proper typing
const deepMergeDefaults = (userSettings: any, defaults: any): UserSettings => {
  if (!userSettings || typeof userSettings !== "object") {
    return defaults as UserSettings;
  }

  const result: UserSettings = {
    tables: {
      defaultConfig: { ...defaults.tables.defaultConfig },
      contextSpecific: { ...defaults.tables.contextSpecific },
    },
    rowActions: userSettings.rowActions || defaults.rowActions,
    columnEnhancements:
      userSettings.columnEnhancements || defaults.columnEnhancements,
  };

  // Merge defaultConfig
  if (userSettings.tables?.defaultConfig) {
    result.tables.defaultConfig = {
      ...defaults.tables.defaultConfig,
      ...userSettings.tables.defaultConfig,
    };
  }

  // Merge contextSpecific tables
  if (userSettings.tables?.contextSpecific) {
    // Start with defaults
    result.tables.contextSpecific = { ...defaults.tables.contextSpecific };

    // Merge user settings over defaults
    Object.keys(userSettings.tables.contextSpecific).forEach(
      (tableName: string) => {
        const userTableConfig = userSettings.tables.contextSpecific[tableName];
        const defaultTableConfig = defaults.tables.contextSpecific[tableName];

        if (defaultTableConfig) {
          // Convert Cell strings to proper format
          const mergedColumns = [
            ...convertColumnCellFunctions(defaultTableConfig.columns || []),
            ...convertColumnCellFunctions(userTableConfig.columns || []),
          ].filter(
            (col, index, self) =>
              index === self.findIndex((c) => c.accessorKey === col.accessorKey)
          );

          result.tables.contextSpecific[tableName] = {
            ...defaultTableConfig,
            ...userTableConfig,
            columns:
              mergedColumns.length > 0
                ? mergedColumns
                : defaultTableConfig.columns,
            initialState: {
              ...defaultTableConfig.initialState,
              ...userTableConfig.initialState,
              pagination: {
                pageSize: 500, // Default to 500 as per requirements
                pageIndex: 0,
                ...defaultTableConfig.initialState?.pagination,
                ...userTableConfig.initialState?.pagination,
              },
            },
            sortingConfig: {
              ...defaultTableConfig.sortingConfig,
              ...userTableConfig.sortingConfig,
            },
          };
        } else {
          // If no default exists, use user configuration with Cell conversion
          result.tables.contextSpecific[tableName] = {
            ...userTableConfig,
            columns: convertColumnCellFunctions(userTableConfig.columns || []),
          };
        }
      }
    );
  }

  return result;
};

// Async Thunks
export const fetchUserPreferences = createAsyncThunk<any>(
  "userPreferences/fetchUserPreferences",
  async (_, { rejectWithValue }) => {
    const userId = getUserId();
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/${userId}`);

      // Merge with default configuration to ensure all required fields exist
      const userSettings = response.data.data.user_settings || {};

      // Deep merge with defaultTableData to ensure all table configurations exist
      const mergedSettings = deepMergeDefaults(userSettings, defaultTableData);

      return mergedSettings;
    } catch (error: any) {
      // If API fails, return default configuration
      console.warn("Failed to fetch user preferences, using defaults:", error);
      return defaultTableData;
    }
  }
);

export const savePreferences = createAsyncThunk<any, any>(
  "userPreferences/savePreferences",
  async (userSettings: any, { dispatch, rejectWithValue }) => {
    const userId = getUserId();
    try {
      const response = await axiosInstance.patch(`${API_BASE_URL}/${userId}`, {
        user_settings: userSettings,
      });

      // After saving, update local state
      dispatch(fetchUserPreferences());

      return { message: response.data.message };
    } catch (error: any) {
      console.error("Error saving preferences:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    // Set column order for a specific table
    setTableColumnOrder: (state, action) => {
      const { tableName, columnOrder } = action.payload;

      if (!state.preferences.tables.contextSpecific[tableName]) {
        state.preferences.tables.contextSpecific[tableName] = {
          columns: [],
          initialState: {},
        };
      }

      state.preferences.tables.contextSpecific[tableName].initialState = {
        ...state.preferences.tables.contextSpecific[tableName].initialState,
        columnOrder,
      };
    },

    // Set sorting state for a specific table
    setTableSorting: (state, action) => {
      const { tableName, sorting } = action.payload;

      if (!state.preferences.tables.contextSpecific[tableName]) {
        state.preferences.tables.contextSpecific[tableName] = {
          columns: [],
          initialState: {},
        };
      }

      state.preferences.tables.contextSpecific[tableName].initialState = {
        ...state.preferences.tables.contextSpecific[tableName].initialState,
        sorting,
      };
    },

    // Set pagination state for a specific table
    setTablePagination: (state, action) => {
      const { tableName, pagination } = action.payload;

      if (!state.preferences.tables.contextSpecific[tableName]) {
        state.preferences.tables.contextSpecific[tableName] = {
          columns: [],
          initialState: {},
        };
      }

      state.preferences.tables.contextSpecific[tableName].initialState = {
        ...state.preferences.tables.contextSpecific[tableName].initialState,
        pagination: {
          ...state.preferences.tables.contextSpecific[tableName].initialState
            ?.pagination,
          ...pagination,
        },
      };
    },

    // Reset a specific table to its default configuration
    resetTableToDefault: (state, action) => {
      const { tableName } = action.payload;
      const defaultConfig = (defaultTableData as any).tables.contextSpecific[
        tableName
      ];

      if (defaultConfig) {
        state.preferences.tables.contextSpecific[tableName] = {
          ...defaultConfig,
          columns: convertColumnCellFunctions(defaultConfig.columns || []),
          initialState: {
            ...defaultConfig.initialState,
            pagination: {
              ...defaultConfig.initialState?.pagination,
              pageSize: 500, // Default to 500 as per requirements
              pageIndex: 0,
            },
          },
        };
      }
    },

    // Reset all tables to default configuration
    resetAllTablesToDefault: (state) => {
      state.preferences = {
        tables: {
          defaultConfig: { ...(defaultTableData as any).tables.defaultConfig },
          contextSpecific: {},
        },
      };

      // Re-initialize all contextSpecific tables with defaults
      Object.keys((defaultTableData as any).tables.contextSpecific).forEach(
        (tableName: string) => {
          const defaultConfig = (defaultTableData as any).tables
            .contextSpecific[tableName];
          state.preferences.tables.contextSpecific[tableName] = {
            ...defaultConfig,
            columns: convertColumnCellFunctions(defaultConfig.columns || []),
            initialState: {
              ...defaultConfig.initialState,
              pagination: {
                ...defaultConfig.initialState?.pagination,
                pageSize: 500, // Default to 500 as per requirements
                pageIndex: 0,
              },
            },
          };
        }
      );
    },

    // Clear all user preferences (use defaults)
    clearUserPreferences: (state) => {
      state.preferences = {
        tables: {
          defaultConfig: { ...(defaultTableData as any).tables.defaultConfig },
          contextSpecific: {},
        },
      };
    },

    // Update column visibility for a table
    setColumnVisibility: (state, action) => {
      const { tableName, columnVisibility } = action.payload;

      if (!state.preferences.tables.contextSpecific[tableName]) {
        state.preferences.tables.contextSpecific[tableName] = {
          columns: [],
          initialState: {},
        };
      }

      state.preferences.tables.contextSpecific[tableName].initialState = {
        ...state.preferences.tables.contextSpecific[tableName].initialState,
        columnVisibility,
      };
    },

    // Update a specific table's configuration
    updateTableConfig: (state, action) => {
      const { tableName, config } = action.payload;
      state.preferences.tables.contextSpecific[tableName] = {
        ...state.preferences.tables.contextSpecific[tableName],
        ...config,
        columns: convertColumnCellFunctions(
          config.columns ||
            state.preferences.tables.contextSpecific[tableName]?.columns ||
            []
        ),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreferences.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.status = "succeeded";

        // Use deep merge to ensure all required fields exist
        const mergedPreferences: any = deepMergeDefaults(
          action.payload,
          defaultTableData
        );

        state.preferences = mergedPreferences;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;

        // On failure, use default configuration
        const defaultPreferences = deepMergeDefaults({}, defaultTableData);
        state.preferences = defaultPreferences;
      })
      .addCase(savePreferences.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(savePreferences.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(savePreferences.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

// Selectors
export const selectTableColumns = (tableName: string) => (state: RootState) =>
  state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
    ?.columns || [];

export const selectTableColumnOrder =
  (tableName: string) => (state: RootState) =>
    state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
      ?.initialState?.columnOrder || [];

export const selectTableSorting = (tableName: string) => (state: RootState) =>
  state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
    ?.initialState?.sorting || [];

export const selectTablePagination =
  (tableName: string) => (state: RootState) =>
    state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
      ?.initialState?.pagination || { pageIndex: 0, pageSize: 500 };

export const selectTableSortingConfig =
  (tableName: string) => (state: RootState) =>
    state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
      ?.sortingConfig;

export const selectTableInitialState =
  (tableName: string) => (state: RootState) =>
    state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
      ?.initialState || {};

export const selectColumnVisibility =
  (tableName: string) => (state: RootState) =>
    state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName]
      ?.initialState?.columnVisibility || {};

export const selectDefaultConfig = (state: RootState) =>
  state.userPreferences?.preferences?.tables?.defaultConfig || {};

export const selectPreferenceStatus = (state: RootState) =>
  state.userPreferences?.status || "idle";

export const selectAllPreferences = (state: RootState) =>
  state.userPreferences?.preferences;

export const selectPreferencesError = (state: RootState) =>
  state.userPreferences?.error;

export const selectTableConfig = (tableName: string) => (state: RootState) =>
  state.userPreferences?.preferences?.tables?.contextSpecific?.[tableName];

// Export actions
export const {
  setTableColumnOrder,
  setTableSorting,
  setTablePagination,
  resetTableToDefault,
  resetAllTablesToDefault,
  clearUserPreferences,
  setColumnVisibility,
  updateTableConfig,
} = userPreferencesSlice.actions;

export default userPreferencesSlice.reducer;
